import { useSyncExternalStore, useCallback, useEffect } from 'react'

/**
 * GENERIC FILTER PERSISTENCE HOOK
 *
 * This hook provides localStorage persistence for any component's filters.
 * It uses React 18's useSyncExternalStore for cross-tab synchronization.
 *
 * How it works:
 * 1. Creates an external store that manages filter state
 * 2. Automatically saves/loads from localStorage
 * 3. Syncs changes across browser tabs
 * 4. Provides type-safe interface for filter updates
 *
 * Usage:
 * const { filters, updateQuery, updateFilter } = useFiltersPersistence({
 *   storageKey: 'my-component-filters',
 *   defaultFilters: { query: '', status: 'all' }
 * })
 */

// Generic filters state interface - all filter objects must extend this
export interface FiltersState<T = Record<string, any>> {
  query: string // Search query - required for all filters
  [key: string]: any // Allow any additional filter keys (status, category, etc.)
}

// Configuration interface for the hook
export interface FiltersConfig<T> {
  storageKey: string // Unique key for localStorage (e.g., 'campaigns-filters')
  defaultFilters: T // Default state when no localStorage data exists
}

/**
 * EXTERNAL STORE CLASS
 *
 * This class manages the filter state outside of React's state system.
 * It handles localStorage read/write and notifies React components of changes.
 *
 * Why external store?
 * - Allows multiple components to share the same filter state
 * - Survives component unmounts/remounts
 * - Enables cross-tab synchronization
 * - Works with React's concurrent rendering
 */
class GenericFiltersStore<T extends FiltersState> {
  private listeners = new Set<() => void>() // Components that listen to state changes
  private filters: T // Current filter state
  private storageKey: string // localStorage key
  private defaultFilters: T // Fallback when localStorage is empty

  constructor(config: FiltersConfig<T>) {
    this.storageKey = config.storageKey
    this.defaultFilters = config.defaultFilters
    this.filters = this.getStoredFilters() // Load initial state from localStorage
  }

  // Load filters from localStorage, with fallback to defaults
  private getStoredFilters(): T {
    try {
      const stored = localStorage.getItem(this.storageKey)
      if (stored) {
        const parsed = JSON.parse(stored)
        // Merge with defaults to ensure all required fields exist
        // This protects against schema changes or corrupted data
        return {
          ...this.defaultFilters,
          ...parsed,
        }
      }
    } catch (error) {
      console.warn(
        `Failed to load filters from localStorage (${this.storageKey}):`,
        error,
      )
    }
    return this.defaultFilters
  }

  // Save current filters to localStorage and notify other tabs
  private saveFilters() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.filters))

      // Dispatch storage event for cross-tab sync
      // This makes filters sync between browser tabs in real-time
      window.dispatchEvent(
        new StorageEvent('storage', {
          key: this.storageKey,
          newValue: JSON.stringify(this.filters),
          oldValue: null,
        }),
      )
    } catch (error) {
      console.warn(
        `Failed to save filters to localStorage (${this.storageKey}):`,
        error,
      )
    }
  }

  // React hook integration methods - used by useSyncExternalStore

  subscribe = (listener: () => void) => {
    // Register a component to listen for state changes
    this.listeners.add(listener)
    return () => this.listeners.delete(listener) // Cleanup function
  }

  getSnapshot = (): T => {
    // Return current state for React rendering
    return this.filters
  }

  getServerSnapshot = (): T => {
    // For SSR compatibility - return safe default state
    return this.defaultFilters
  }

  // Public API methods for updating filters

  updateQuery = (query: string) => {
    // Update search query and persist to localStorage
    if (this.filters.query !== query) {
      this.filters = { ...this.filters, query }
      this.saveFilters()
      this.listeners.forEach((listener) => listener()) // Notify React components
    }
  }

  updateFilter = (key: string, value: any) => {
    // Update any filter field (status, category, etc.) and persist
    if (this.filters[key] !== value) {
      this.filters = {
        ...this.filters,
        [key]: value,
      }
      this.saveFilters()
      this.listeners.forEach((listener) => listener()) // Notify React components
    }
  }

  clearFilters = () => {
    // Reset all filters to default state and persist
    this.filters = this.defaultFilters
    this.saveFilters()
    this.listeners.forEach((listener) => listener()) // Notify React components
  }

  // Listen for storage events from other tabs (cross-tab synchronization)
  handleStorageEvent = (event: StorageEvent) => {
    if (event.key === this.storageKey && event.newValue) {
      try {
        const newFilters = JSON.parse(event.newValue)
        this.filters = { ...this.defaultFilters, ...newFilters }
        this.listeners.forEach((listener) => listener()) // Update all React components
      } catch (error) {
        console.warn('Failed to parse storage event:', error)
      }
    }
  }
}

/**
 * STORE INSTANCES CACHE
 *
 * We cache store instances by storageKey to ensure:
 * 1. Multiple components using same storageKey share the same store
 * 2. Store persists across component unmounts/remounts
 * 3. Only one localStorage listener per storage key
 */
const storeInstances = new Map<string, GenericFiltersStore<any>>()
const storeRefCounts = new Map<string, number>()

/**
 * MAIN HOOK - useFiltersPersistence
 *
 * This is what components actually use. It:
 * 1. Creates or retrieves a store instance
 * 2. Connects React component to the external store
 * 3. Provides convenient methods for updating filters
 *
 * Example usage:
 * const { filters, updateQuery, updateFilter } = useFiltersPersistence({
 *   storageKey: 'campaigns-filters',
 *   defaultFilters: { query: '', status: { active: false } }
 * })
 */
export const useFiltersPersistence = <T extends FiltersState>(
  config: FiltersConfig<T>,
) => {
  // Get or create store instance for this config
  const getStore = useCallback(() => {
    if (!storeInstances.has(config.storageKey)) {
      const store = new GenericFiltersStore(config)
      storeInstances.set(config.storageKey, store)
      storeRefCounts.set(config.storageKey, 0)

      // Listen for storage events from other tabs
      if (typeof window !== 'undefined') {
        window.addEventListener('storage', store.handleStorageEvent)
      }
    }
    return storeInstances.get(config.storageKey)!
  }, [config.storageKey, config.defaultFilters])

  const store = getStore()

  // Track mount/unmount to clean up unused stores and their event listeners
  useEffect(() => {
    const key = config.storageKey
    storeRefCounts.set(key, (storeRefCounts.get(key) || 0) + 1)
    return () => {
      const count = (storeRefCounts.get(key) || 1) - 1
      storeRefCounts.set(key, count)
      if (count <= 0) {
        const s = storeInstances.get(key)
        if (s && typeof window !== 'undefined') {
          window.removeEventListener('storage', s.handleStorageEvent)
        }
        storeInstances.delete(key)
        storeRefCounts.delete(key)
      }
    }
  }, [config.storageKey])

  // Connect React component to external store using React 18's useSyncExternalStore
  const filters = useSyncExternalStore(
    store.subscribe, // Function to subscribe to store changes
    store.getSnapshot, // Function to get current store state
    store.getServerSnapshot, // Function to get initial state for SSR
  )

  // Wrapped update methods with useCallback for performance
  const updateQuery = useCallback(
    (query: string) => {
      store.updateQuery(query)
    },
    [store],
  )

  const updateFilter = useCallback(
    (key: string, value: any) => {
      store.updateFilter(key, value)
    },
    [store],
  )

  const clearFilters = useCallback(() => {
    store.clearFilters()
  }, [store])

  return {
    filters, // Current filter state (reactive)
    updateQuery, // Function to update search query
    updateFilter, // Function to update any filter field
    clearFilters, // Function to reset all filters
  }
}
